<template>
  <widget-template-card
    :key="widget_key"
    :template="template"
    :is_selected="is_selected"
    :compose_preview_settings="composePreviewSettings"
    :build_src="buildIframeSrc"
    :send_settings="sendSettingsToWidget"
    :send_autofill_test_data="sendAutoFillTestData"
    :width_multiplier="computed_width_multiplier"
    :widget_props="widget_props"
    :is_only_option="is_only_option"
    preview_mode="tablet-landscape"
    current_page="form"
    preview_area_class="order-confirmation-preview-area"
    :height="height_based_on_layout"
    @select="$emit('select', template)"
  />
</template>

<script>
import WidgetTemplateCard from '@/components/widget/widget-template-card.vue';
import { buildWidgetIframeSrc, sendSettingsToWidget, sendAutoFillTestData } from '@/js/widget/widget-preview.js';
import { composeOrderConfirmationPreviewParams } from '@/js/widget/order-confirmation-preview-composer.js';

/**
 * Order Confirmation Template Card
 *
 * Displays a preview of an order confirmation template in a card format.
 * Used in template galleries for template selection.
 */
export default {
  name: 'mOrderConfirmationTemplateCard',

  components: {
    WidgetTemplateCard,
  },

  props: {
    template: {
      type: Object,
      required: true,
    },

    is_selected: {
      type: Boolean,
      default: false,
    },

    theme: {
      type: Object,
      default: () => ({}),
    },

    organization_name: {
      type: String,
      default: 'Your Organization',
    },

    background_image_url: {
      type: String,
      default: null,
    },

    side_image_url: {
      type: String,
      default: null,
    },

    logo_url: {
      type: String,
      default: null,
    },

    use_logo: {
      type: Boolean,
      default: false,
    },

    order_confirmation_settings: {
      type: Object,
      default: () => ({}),
    },

    comp_custom_fonts: {
      type: Object,
      default: () => ({}),
    },

    is_only_option: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      widget_key: 0,
      root_width: 0,
      resize_observer: null,
    };
  },

  computed: {
    widget_props() {
      return {
        theme: this.theme,
        organization_name: this.organization_name,
        background_image_url: this.background_image_url,
        side_image_url: this.side_image_url,
        logo_url: this.logo_url,
        use_logo: this.use_logo,
        order_confirmation_settings: this.order_confirmation_settings,
        comp_custom_fonts: this.comp_custom_fonts,
        is_test_submission: true,
      };
    },

    computed_width_multiplier() {
      if (this.root_width > 500) return 1;
      if (this.root_width >= 400 && this.root_width <= 500) return 1.33;
      return 1.5;
    },

    layout_mode() {
      return this.template?.template_overrides?.layout_mode || 'single-column';
    },

    height_based_on_layout() {
      const layout_heights = {
        'side-by-side': 1400,
        'single-column': this.use_logo && this.logo_url ? 1050 : 950,
      };
      return layout_heights[this.layout_mode] || null;
    },
  },

  watch: {
    logo_url() {
      this.widget_key++;
    },
    use_logo() {
      this.widget_key++;
    },
  },

  mounted() {
    this.updateRootWidth();
    this.resize_observer = new ResizeObserver(() => {
      this.updateRootWidth();
    });
    this.resize_observer.observe(this.$el);
  },

  beforeDestroy() {
    if (this.resize_observer) {
      this.resize_observer.disconnect();
    }
  },

  methods: {
    updateRootWidth() {
      if (this.$el) {
        this.root_width = this.$el.clientWidth;
      }
    },
    composePreviewSettings(props) {
      return composeOrderConfirmationPreviewParams(props);
    },

    buildIframeSrc(preview_mode, is_development) {
      return buildWidgetIframeSrc({
        widget_name: 'order-confirmation',
        preview_mode,
        is_development,
        dev_port: 5176, // Order Confirmation uses port 5176
      });
    },

    sendSettingsToWidget,
    sendAutoFillTestData,
  },
};
</script>

<style scoped lang="scss">
.order-confirmation-preview-area {
  padding: 16px 16px 0 16px;
  flex-shrink: 0;
}
</style>